import {Observable} from '@PosterWhiteboard/observable';
import type {Item, ItemFabricObject} from '@PosterWhiteboard/items/item/item.class';
import type {FabricObject, Path} from '@postermywall/fabricjs-2';
import {Rect, util} from '@postermywall/fabricjs-2';
import {getScaledManualHorizontalPadding} from '@PosterWhiteboard/items/item/item.library';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {PosterEvents} from '@PosterWhiteboard/poster/poster.types';
import type {AnimationObject} from '../classes/item-animation.class';
import {AnimationSpeed, getAnimationTime, PositionType, SlideType} from '../classes/item-animation.class';
import {noop} from '@Utils/general.util';
import {AnimationType, ITEM_TYPE, type BaseItemObject, type ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {DeepPartial} from '@/global';

export interface ItemAnimationObject {
  type: AnimationType;
  direction: SlideType | PositionType;
  speed: AnimationSpeed;
}

export interface AnimationConfig {
  isTransition: boolean;
  animation?: DeepPartial<AnimationObject>;
  onComplete?: () => void;
}

const NUMBER_THOUSAND = 1000;
const ROTATE_ANIMATION_ANGLE = 135;
const MAX_LARGER_DIMENSION_SKEW_VALUE = 40;
const MAX_SMALLER_DIMENSION_SKEW_VALUE = 15;

export class Animation extends Observable<PosterEvents> {
  public item: Item;
  public type = AnimationType.NONE;
  public speed = AnimationSpeed.MEDIUM;
  public direction: SlideType | PositionType = SlideType.RIGHT;

  public isAnimationPlaying = false;
  private isAnimationOver = false;
  private initSetFrame = false;
  private isFinalFrameSet = false;
  protected isTransition = false;
  protected viewComponentOpacitySnapshot = 0;

  protected animatingItem!: ItemFabricObject;
  protected clippedItem!: ItemFabricObject;
  protected clipPath!: Rect;
  protected block!: Rect;

  protected onStartValues = {};
  public onCompleteValues: Record<string, number> = {};
  public startingAnimationValues: Record<string, number> = {};
  protected endingAnimationValues: Record<string, number> = {};
  protected defaultValues: Record<string, number> = {};
  private onAnimationChange: () => void = noop;
  public internalOnComplete: () => void = noop;
  private readonly callback: () => void = noop;
  public readonly syncAnimationToPage;

  constructor(item: Item, opts?: AnimationConfig) {
    super();
    this.item = item;
    if (opts?.onComplete) {
      this.callback = opts.onComplete;
    }
    if (opts?.isTransition) {
      this.isTransition = opts.isTransition;
    }
    if (opts?.animation) {
      this.copyVals(opts.animation);
    }

    this.syncAnimationToPage = this.syncAnimation.bind(this);
    this.item.page.on('time:updated', this.syncAnimationToPage);
  }

  public dispose(): void {
    this.item.page.off('time:updated', this.syncAnimationToPage);
  }

  public hasAnimation(): boolean {
    return this.type !== AnimationType.NONE;
  }

  public syncAnimation(pageTime: number): void {
    if (pageTime < 0) {
      return;
    }

    if (this.item.isSlideshowSlide() || this.item.isTranscript()) {
      return;
    }

    if (!this.item.page.clock.isPaused() && this.hasAnimation() && this.timeForAnimation(pageTime) && !this.isAnimationPlaying) {
      this.start();
    }
  }

  public getAnimatingSlide(): Item {
    return this.item;
  }

  public getAnimatingItem(): Item {
    if ((this.shouldClipForAnimation(this.type) || isBlockAnimation(this.type)) && this.item.isSlideshow()) {
      return this.getAnimatingSlide();
    }
    return this.item;
  }

  public timeForAnimation(_time: number): boolean {
    return false;
  }

  public restoreDefaultStateBeforeAnimating(): void {}

  public initAnimation(): void {}

  public updateOpacityOnBlockEnd(): void {}

  public blockAnimationOnChange(): void {}

  public updateOpacityOnBlockInit(): void {}

  public setInitialBlockPositionParams(): void {}

  public getAnimationTimeForEaseFunction(_time: number): number {
    return 0;
  }

  public getAnimationTimeForFinalFrame(): number {
    return 0;
  }

  public getAnimationBlockCoordinates(): Record<string, number> {
    return {};
  }

  public getAnimationBounceCoordinates(): Record<string, number> {
    return {};
  }

  public getAnimationFadeValues(): Record<string, number> {
    return {
      opacity: 0,
      scaleX: this.animatingItem.scaleX * 0.95,
      scaleY: this.animatingItem.scaleY * 0.95,
    };
  }

  public getAnimationPanValues(): Record<string, number> {
    return {};
  }

  public getAnimationSlideValues(): Record<string, number> {
    return {};
  }

  public getAnimationWipeCoordinates(): Record<string, number> {
    return {};
  }

  public getAnimationZoomScaleValues(): Record<string, number> {
    return {};
  }

  public getAnimationPopValues(): Record<string, number> {
    return {};
  }

  public getAnimationPeekValues(): Record<string, number> {
    return {};
  }

  public getEaseFunctionForPopAndJello(): (timeElapsed: number, startValue: number, byValue: number, duration: number) => number {
    return util.ease.easeOutCubic;
  }

  public getAnimationTumbleValues(): Record<string, number> {
    const bound = this.animatingItem.getBoundingRect();
    const animationValues: Record<string, number> = {
      opacity: 0,
    };

    switch (this.direction) {
      case PositionType.TOP:
        animationValues.top = this.item.fabricObject.top - (bound.top + bound.height) - 10;
        break;

      case PositionType.BOTTOM:
        animationValues.top = this.item.page.fabricCanvas.height / this.item.page.poster.scaling.scale - bound.top + this.item.fabricObject.top - 10;
        break;

      case PositionType.LEFT:
        animationValues.left = -bound.height - 10;
        animationValues.angle = this.animatingItem.angle - 90;
        break;

      case PositionType.RIGHT:
        animationValues.left = this.item.page.fabricCanvas.width / this.item.page.poster.scaling.scale + bound.width + 10;
        animationValues.angle = this.animatingItem.angle + 180;
        break;

      case PositionType.TOP_LEFT:
        animationValues.left = -bound.width - 10;
        animationValues.top = -bound.height - 10;
        animationValues.angle = this.animatingItem.angle - 180;
        break;

      case PositionType.BOTTOM_LEFT:
        animationValues.left = -bound.width - 10;
        animationValues.top = this.item.page.fabricCanvas.height / this.item.page.poster.scaling.scale + bound.height + 10;
        animationValues.angle = this.animatingItem.angle + 180;
        break;

      case PositionType.TOP_RIGHT:
        animationValues.left = this.item.page.fabricCanvas.width / this.item.page.poster.scaling.scale + bound.width + 10;
        animationValues.top = -bound.height - 10;
        animationValues.angle = this.animatingItem.angle - 180;
        break;

      default: // case PositionType.BOTTOM_RIGHT:
        animationValues.left = this.item.page.fabricCanvas.width / this.item.page.poster.scaling.scale + bound.width + 10;
        animationValues.top = this.item.page.fabricCanvas.height / this.item.page.poster.scaling.scale + bound.height + 10;
        animationValues.angle = this.animatingItem.angle + 180;
        break;
    }
    return animationValues;
  }

  public start(): void {
    this.isAnimationPlaying = true;
    this.isAnimationOver = false;
    this.viewComponentOpacitySnapshot = this.item.fabricObject.opacity;
    this.restoreDefaultStateBeforeAnimating();
    if (this.type === AnimationType.BLOCK) {
      this.initBlockAnimationItems();
      this.item.fabricObject.dirty = true;
      this.setInternalOnCompleteByAnimationType();
    } else if (this.shouldClipForAnimation(this.type)) {
      this.setClippedItem();
      this.initClipPathOnObjectAnimation();
      this.setInternalOnCompleteByAnimationType();
    } else if (!this.item.page.poster.isHighRes && this.isComplex()) {
      this.initComplexItems();
    } else {
      this.animatingItem = this.item.fabricObject;
    }
    this.startAnimation();
  }

  public initComplexItems(): void {
    const {left, top, opacity, angle} = this.item.fabricObject;

    this.item.fabricObject.set({angle: this.item.isTab() ? 180 : 0});
    this.item.fabricObject.set({visible: true});
    const clone = this.item.fabricObject.cloneAsImage({});
    this.item.fabricObject.set('opacity', 0);
    this.item.fabricObject.set({angle: angle});
    this.item.fabricObject.setCoords();

    clone.set({top, left, angle: angle, selectable: false});
    this.item.page.fabricCanvas.add(clone);
    this.animatingItem = clone;
    this.item.page.fabricCanvas.moveObjectTo(this.animatingItem as FabricObject, this.item.page.fabricCanvas.getObjects().indexOf(this.item.fabricObject));
    this.internalOnComplete = (): void => {
      this.item.page.fabricCanvas.remove(clone);
      this.item.fabricObject.set('opacity', opacity);
    };
  }

  public startAnimation(): void {
    this.initAnimation();

    let i = 0;
    this.animatingItem.animate(this.endingAnimationValues, {
      duration: this.getAnimationTimeForItem() * NUMBER_THOUSAND,
      easing: this.getEaseFunctionForAnimationStyle(),
      onComplete: () => {
        i += 1;
        if (i === Object.keys(this.endingAnimationValues).length) {
          this.onAnimationComplete();
        }
      },
      onChange: this.onAnimationChange.bind(this),
      abort: () => {
        return this.isAnimationOver;
      },
    });
  }

  protected onAnimationComplete(): void {
    const item = this.getAnimatingItem();
    item.fabricObject.set(this.onCompleteValues);
    if (item.fabricObject.opacity !== this.viewComponentOpacitySnapshot) {
      item.fabricObject.set('opacity', this.viewComponentOpacitySnapshot);
    }
    if (this.internalOnComplete) {
      this.internalOnComplete();
    }
    this.isAnimationPlaying = false;
    this.viewComponentOpacitySnapshot = 0;
    this.callback();
  }

  public setInternalOnCompleteByAnimationType(): void {
    switch (this.type) {
      case AnimationType.BLOCK:
        this.internalOnComplete = (): void => {
          this.updateOpacityOnBlockEnd();
          this.item.fabricObject.dirty = true;
          this.item.page.fabricCanvas.remove(this.block);
        };
        break;

      case AnimationType.ZOOM:
      case AnimationType.WIPE:
      case AnimationType.WIPE_DOWN:
      case AnimationType.WIPE_UP:
      case AnimationType.WIPE_LEFT:
      case AnimationType.WIPE_RIGHT:
      case AnimationType.PEEK:
      case AnimationType.PEEK_DOWN:
      case AnimationType.PEEK_UP:
      case AnimationType.PEEK_LEFT:
      case AnimationType.PEEK_RIGHT:
        this.internalOnComplete = (): void => {
          const item = this.getAnimatingItem();
          if (item.isTextSlide() || item.isText()) {
            this.removeClipPathFromText();
          } else {
            this.clippedItem.set({clipPath: null});
            if (this.clippedItem !== item.fabricObject) {
              item.page.fabricCanvas.remove(this.clippedItem);
              item.fabricObject.set({opacity: this.viewComponentOpacitySnapshot});
            }
          }
          item.fabricObject.setCoords();
        };
        break;

      default:
        break;
    }
  }

  public isComplex(): boolean {
    if (this.item.fabricObject.get('type') === 'group') {
      return !this.item.isSlideshow() && !this.item.isText() && !this.item.isTextSlide();
    }

    if (this.item.fabricObject.get('type') === 'path') {
      // TODO: Remove this when we upgrade to Fabric v6.
      const fabricObject = this.item.fabricObject as Path;
      return fabricObject.path.length > 20;
    }

    return false;
  }

  public stop(): void {
    this.isAnimationOver = true;
    this.animatingItem.set(this.endingAnimationValues);
    this.onAnimationComplete();
  }

  public getAnimationValues(): Record<string, number> {
    let animationValues: Record<string, number> = {};
    let theta;
    const poster = window.posterEditor?.whiteboard;

    if (poster) {
      switch (this.type) {
        case AnimationType.SLIDE:
          animationValues = this.getAnimationSlideValues();
          break;

        case AnimationType.FADE:
          animationValues = this.getAnimationFadeValues();
          break;

        case AnimationType.TUMBLE:
          animationValues = this.getAnimationTumbleValues();
          break;

        case AnimationType.PAN: {
          animationValues = this.getAnimationPanValues();
          break;
        }

        case AnimationType.BOUNCE_IN_DOWN:
        case AnimationType.BOUNCE:
          animationValues = this.getAnimationBounceCoordinates();
          break;

        case AnimationType.BLOCK:
          animationValues = this.getAnimationBlockCoordinates();
          break;

        case AnimationType.WIPE:
          animationValues = this.getAnimationWipeCoordinates();
          break;

        case AnimationType.SCALE:
        case AnimationType.ZOOM:
          animationValues = this.getAnimationZoomScaleValues();
          break;

        case AnimationType.POP:
          animationValues = this.getAnimationPopValues();
          break;

        case AnimationType.PEEK:
          animationValues = this.getAnimationPeekValues();
          break;

        case AnimationType.JELLO:
          animationValues = {
            opacity: 0,
            skewX:
              this.animatingItem.width > this.animatingItem.height
                ? MAX_LARGER_DIMENSION_SKEW_VALUE
                : Math.max(MAX_SMALLER_DIMENSION_SKEW_VALUE, (this.animatingItem.width / this.animatingItem.height) * MAX_LARGER_DIMENSION_SKEW_VALUE),
            skewY:
              this.animatingItem.width > this.animatingItem.height
                ? Math.max(MAX_SMALLER_DIMENSION_SKEW_VALUE, (this.animatingItem.height / this.animatingItem.width) * MAX_LARGER_DIMENSION_SKEW_VALUE)
                : MAX_LARGER_DIMENSION_SKEW_VALUE,
          };
          break;

        case AnimationType.ROTATE: {
          const initialAngle = this.direction === SlideType.LEFT ? this.animatingItem.angle - ROTATE_ANIMATION_ANGLE : this.animatingItem.angle + ROTATE_ANIMATION_ANGLE;
          animationValues = {
            opacity: 0,
            scaleX: this.animatingItem.scaleX / 2,
            scaleY: this.animatingItem.scaleY / 2,
            angle: initialAngle,
          };
          break;
        }

        case AnimationType.SHRINK:
          animationValues = {
            opacity: 0,
            scaleX: this.animatingItem.scaleX * 2,
            scaleY: this.animatingItem.scaleY * 2,
          };
          break;

        case AnimationType.EXPAND:
          animationValues = {
            opacity: 0,
            scaleX: this.animatingItem.scaleX / 2,
            scaleY: this.animatingItem.scaleY / 2,
          };
          break;

        case AnimationType.PAN_RIGHT:
          animationValues = {
            opacity: 0,
            left: this.item.fabricObject.left - 0.2 * poster.width,
          };
          break;

        case AnimationType.PAN_LEFT:
          animationValues = {
            opacity: 0,
            left: this.item.fabricObject.left + 0.2 * poster.width,
          };
          break;

        case AnimationType.PAN_DOWN:
          animationValues = {
            opacity: 0,
            top: this.item.fabricObject.top - 0.2 * poster.height,
          };
          break;

        case AnimationType.PAN_UP:
          animationValues = {
            opacity: 0,
            top: this.item.fabricObject.top + 0.2 * poster.height,
          };
          break;

        case AnimationType.RISE:
          animationValues = {
            opacity: 0,
            top: this.item.fabricObject.top + 70,
          };
          break;

        case AnimationType.WIPE_DOWN:
          theta = util.degreesToRadians(this.animatingItem.angle);
          animationValues = {
            left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
            top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
          };
          break;

        case AnimationType.WIPE_UP:
          theta = util.degreesToRadians(this.animatingItem.angle);
          animationValues = {
            left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
            top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
          };
          break;

        case AnimationType.WIPE_LEFT:
          theta = util.degreesToRadians(this.animatingItem.angle);
          animationValues = {
            left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
            top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
          };
          break;

        case AnimationType.WIPE_RIGHT:
          theta = util.degreesToRadians(this.animatingItem.angle);
          animationValues = {
            left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
            top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
          };
          break;

        case AnimationType.PEEK_DOWN:
          if (this.clippedItem === this.item.fabricObject) {
            animationValues = {
              top: this.animatingItem.top - this.animatingItem.getScaledHeight(),
            };
          }
          // the case where masked image is cloned as image to be clipped
          else {
            theta = util.degreesToRadians(this.animatingItem.angle);
            animationValues = {
              left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
              top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
            };
          }
          break;

        case AnimationType.PEEK_UP:
          if (this.clippedItem === this.item.fabricObject) {
            animationValues = {
              top: this.animatingItem.top + this.animatingItem.getScaledHeight(),
            };
          }
          // the case where masked image is cloned as image to be clipped
          else {
            theta = util.degreesToRadians(this.animatingItem.angle);
            animationValues = {
              left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
              top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
            };
          }
          break;

        case AnimationType.PEEK_LEFT:
          if (this.clippedItem === this.item.fabricObject) {
            animationValues = {
              left: this.animatingItem.left + this.animatingItem.getScaledWidth(),
            };
          }
          // the case where masked image is cloned as image to be clipped
          else {
            theta = util.degreesToRadians(this.animatingItem.angle);
            animationValues = {
              left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
              top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
            };
          }
          break;

        case AnimationType.PEEK_RIGHT:
          if (this.clippedItem === this.item.fabricObject) {
            animationValues = {
              left: this.animatingItem.left - this.animatingItem.getScaledWidth(),
            };
          }
          // the case where masked image is cloned as image to be clipped
          else {
            theta = util.degreesToRadians(this.animatingItem.angle);
            animationValues = {
              left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
              top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
            };
          }
          break;

        case AnimationType.NONE:
          break;

        default:
          console.error('Unknown animation type: ', this.type);
          break;
      }
    }
    this.setDefaultValues(Object.keys(animationValues));

    return animationValues;
  }

  public setDefaultValues(keys: string[]): void {
    this.defaultValues = {};
    keys.forEach((key) => {
      this.defaultValues[key] = this.animatingItem.get(key);
    });
  }

  private getCommonOnStartValues(): Record<string, number | string> {
    const angle = util.degreesToRadians(this.animatingItem.angle);
    return {
      opacity: this.animatingItem.opacity,
      left: this.animatingItem.left + (Math.cos(angle) * (this.animatingItem.getScaledWidth() / 2) - Math.sin(angle) * (this.animatingItem.getScaledHeight() / 2)),
      top: this.animatingItem.top + (Math.sin(angle) * (this.animatingItem.getScaledWidth() / 2) + Math.cos(angle) * (this.animatingItem.getScaledHeight() / 2)),
      originX: 'center',
      originY: 'center',
      scaleX: this.animatingItem.scaleX,
      scaleY: this.animatingItem.scaleY,
    };
  }

  public getOnStartValues(): Record<string, number | string> {
    let startingValues: Record<string, number | string> = {};

    switch (this.type) {
      case AnimationType.FADE:
      case AnimationType.POP:
      case AnimationType.SHRINK:
      case AnimationType.EXPAND:
      case AnimationType.ZOOM:
      case AnimationType.SCALE:
        startingValues = this.getCommonOnStartValues();
        break;

      case AnimationType.JELLO:
        startingValues = {
          ...this.getCommonOnStartValues(),
          skewX: this.animatingItem.skewX,
          skewY: this.animatingItem.skewY,
        };
        break;

      case AnimationType.ROTATE:
        startingValues = {
          ...this.getCommonOnStartValues(),
          angle: this.animatingItem.angle,
        };
        break;

      case AnimationType.TUMBLE:
        startingValues = {
          top: this.animatingItem.top,
          left: this.animatingItem.left,
          angle: this.animatingItem.angle,
        };
        break;

      case AnimationType.PEEK_DOWN:
      case AnimationType.PEEK_UP:
      case AnimationType.PAN_DOWN:
      case AnimationType.PAN_UP:
        startingValues = {
          top: this.animatingItem.top,
        };
        break;

      case AnimationType.PEEK_LEFT:
      case AnimationType.PEEK_RIGHT:
      case AnimationType.PAN_LEFT:
      case AnimationType.PAN_RIGHT:
        startingValues = {
          left: this.animatingItem.left,
        };
        break;

      case AnimationType.SLIDE:
      case AnimationType.PEEK:
        startingValues = {
          left: this.animatingItem.left,
          top: this.animatingItem.top,
        };
        break;

      case AnimationType.PAN:
      case AnimationType.BOUNCE_IN_DOWN:
      case AnimationType.BOUNCE:
        if (this.direction === PositionType.RIGHT || this.direction === PositionType.LEFT) {
          startingValues = {
            opacity: this.animatingItem.opacity,
            left: this.animatingItem.left,
          };
        }
        if (this.direction === PositionType.TOP || this.direction === PositionType.BOTTOM) {
          startingValues = {
            opacity: this.animatingItem.opacity,
            top: this.animatingItem.top,
          };
        }
        break;

      default:
        break;
    }

    this.setOnCompleteValues(Object.keys(startingValues));
    return startingValues;
  }

  public setOnCompleteValues(keys: string[]): void {
    this.onCompleteValues = {};
    for (const key of keys) {
      this.onCompleteValues[key] = this.getAnimatingItem().fabricObject.get(key);
    }
    if (this.viewComponentOpacitySnapshot) {
      this.onCompleteValues.opacity = this.viewComponentOpacitySnapshot;
    }
  }

  public initClipPathOnObjectAnimation(): void {
    const item = this.getAnimatingItem();
    item.fabricObject.setCoords();

    switch (this.type) {
      case AnimationType.WIPE:
      case AnimationType.WIPE_DOWN:
      case AnimationType.WIPE_UP:
      case AnimationType.WIPE_LEFT:
      case AnimationType.WIPE_RIGHT:
        this.createPaddedObjectSizeClipPath();
        this.animatingItem = this.clipPath;
        break;

      case AnimationType.ZOOM:
      case AnimationType.PEEK:
      case AnimationType.PEEK_DOWN:
      case AnimationType.PEEK_UP:
      case AnimationType.PEEK_LEFT:
      case AnimationType.PEEK_RIGHT:
        this.createSelectorSizeClipPath();
        this.animatingItem = this.clippedItem;
        break;

      default:
        break;
    }

    if (item.isText() || item.isTextSlide()) {
      this.setClipPathOnText();
    } else {
      this.clippedItem.set({clipPath: this.clipPath});
    }
  }

  public setClipPathOnText(): void {
    const item = this.getAnimatingItem();
    if (item.fabricObject.shadow && (item.isText() || item.isTextSlide()) && item.list.hasList()) {
      // TODO: Remove this when we upgrade to Fabric v6.
      const groupObjects = item.fabricObject.getObjects();
      for (const groupObject of groupObjects) {
        groupObject.set({clipPath: this.clipPath});
      }
    } else {
      item.fabricObject.set({clipPath: this.clipPath});
    }
  }

  public createPaddedObjectSizeClipPath(): void {
    const item = this.getAnimatingItem();
    if (item.isText() || item.isTextSlide()) {
      this.createClipPathForText();
    } else if (item.isMediaSlide()) {
      this.createClipPathForMediaSlides();
    } else {
      this.createClipPathForItems();
    }
  }

  public setClippedItem(): void {
    const item = this.getAnimatingItem();
    if ((item.isImage() || item.isImageSlide()) && item.isMasked()) {
      this.viewComponentOpacitySnapshot = item.fabricObject.opacity;

      const clone = item.fabricObject.cloneAsImage({});
      const fabricObject = item.fabricObject.group ?? item.fabricObject;
      if (fabricObject) {
        item.fabricObject.set('opacity', 0);
        clone.set({
          angle: fabricObject.angle,
          selectable: false,
        });
        if (item.isImageSlide()) {
          clone.set({
            scaleX: fabricObject.scaleX,
            scaleY: fabricObject.scaleY,
            ...item.mediaSlide.getMediaSlideAbsolutePosition(),
          });
        } else {
          clone.set({
            left: item.getFabricObjectAbsoluteLeft(),
            top: item.getFabricObjectAbsoluteTop(),
          });
        }
        clone.setCoords();
        this.item.page.fabricCanvas.add(clone);
        this.clippedItem = clone;
        this.item.page.fabricCanvas.moveObjectTo(this.clippedItem, this.item.page.fabricCanvas.getObjects().indexOf(fabricObject) + 1);
      }
    } else {
      this.clippedItem = item.fabricObject;
    }
  }

  public removeClipPathFromText(): void {
    const item = this.getAnimatingItem();
    if (item.fabricObject.shadow && (item.isText() || item.isTextSlide()) && item.list.hasList()) {
      // TODO: Remove this when we upgrade to Fabric v6.
      const groupObjects = item.fabricObject.getObjects();
      for (const groupObject of groupObjects) {
        groupObject.set({clipPath: null});
      }
    } else {
      item.fabricObject.set({clipPath: null});
    }
  }

  public initBlockAnimationItems(): void {
    const item = this.getAnimatingItem();
    this.onAnimationChange = this.blockAnimationOnChange.bind(this);

    const textItem = item.isText() || item.isTextSlide() ? item : undefined;
    const isItemText = !!textItem;
    const isTextEmpty = textItem?.isTextEmpty();
    const fill = item.isText() || item.isTextSlide() || item.isImageSlide() || item.isVideoSlide() ? item?.getFill() : '';

    this.createClipPathForBlock();

    this.block = new Rect({
      angle: this.clipPath.angle,
      width: isItemText && isTextEmpty ? 0 : this.clipPath.width,
      height: this.clipPath.height,
      fill,
      clipPath: this.clipPath,
      selectable: false,
    });
    this.setInitialBlockPositionParams();
    this.item.page.fabricCanvas.add(this.block);
    this.updateOpacityOnBlockInit();
    this.animatingItem = this.block;
    const itemToMove = item.fabricObject.group ?? item.fabricObject;
    this.item.page.fabricCanvas.moveObjectTo(this.animatingItem, this.item.page.fabricCanvas.getObjects().indexOf(itemToMove) + 1);
  }

  public createClipPathForBlock(): void {
    const item = this.getAnimatingItem();
    if (item.isText() || item.isTextSlide()) {
      if (item.hasBackground()) {
        this.createSelectorSizeClipPath();
      } else {
        this.initClipPathProperties();
        const fabricObject = item.fabricObject.group ?? item.fabricObject;
        this.clipPath.set({
          width: item.fabricTextbox.width * fabricObject.scaleX + item.getBulletsWidth(),
          height: item.fabricTextbox.height * fabricObject.scaleY,
        });
        this.clipPath.set(item.getTextChildAbsolutePosition(this.clipPath));
        this.clipPath.setCoords();
      }
    } else if (item.isMediaSlide()) {
      this.createClipPathForMediaSlides();
    }
  }

  public createSelectorSizeClipPath(): void {
    const item = this.getAnimatingItem();
    this.initClipPathProperties();
    const fabricObject = item.fabricObject.group ?? item.fabricObject;
    if (fabricObject) {
      this.clipPath.set({
        width: fabricObject.getScaledWidth() + fabricObject.strokeWidth / fabricObject.scaleX,
        height: fabricObject.getScaledHeight() + fabricObject.strokeWidth / fabricObject.scaleY,
        left: item.getFabricObjectAbsoluteLeft(),
        top: item.getFabricObjectAbsoluteTop(),
      });
      this.clipPath.setCoords();
    }
  }

  public initClipPathProperties(): void {
    const item = this.getAnimatingItem();
    const fabricObject = item.fabricObject.group ?? item.fabricObject;

    if (fabricObject) {
      this.clipPath = new Rect({
        angle: fabricObject.angle,
        absolutePositioned: true,
      });
    }
  }

  private createClipPathForText(): void {
    const item = this.getAnimatingItem();
    if (!item.isText() && !item.isTextSlide()) {
      return;
    }
    if (item.hasBackground()) {
      this.createSelectorSizeClipPath();
    } else {
      this.initClipPathProperties();
      const fabricObject = item.fabricObject.group ?? item.fabricObject;
      this.clipPath.set({
        width:
          item.fabricTextbox.width * fabricObject.scaleX +
          getScaledManualHorizontalPadding(this.clipPath.scaleX) +
          item.fabricObject.strokeWidth / item.fabricObject.scaleX +
          item.getBulletsWidth(),
        height: item.fabricTextbox.height * fabricObject.scaleY + getScaledManualHorizontalPadding(this.clipPath.scaleY) + item.fabricObject.strokeWidth / item.fabricObject.scaleY,
      });
      this.clipPath.set(item.getPaddedTextChildAbsolutePosition(this.clipPath));
      this.clipPath.setCoords();
    }
  }

  private createClipPathForItems(): void {
    const item = this.getAnimatingItem();
    this.initClipPathProperties();
    this.clipPath.set({
      width: item.fabricObject.getScaledWidth() + item.fabricObject.strokeWidth / item.fabricObject.scaleX,
      height: item.fabricObject.getScaledHeight() + item.fabricObject.strokeWidth / item.fabricObject.scaleY,
      left: item.getFabricObjectAbsoluteLeft(),
      top: item.getFabricObjectAbsoluteTop(),
    });
    this.clipPath.setCoords();
  }

  public createClipPathForMediaSlides(): void {
    this.initClipPathProperties();
    const mediaSlideItem = this.getAnimatingItem() as ImageSlideItem | VideoSlideItem;
    const fabricObject = this.item.fabricObject.group ?? this.item.fabricObject;
    this.clipPath.set({
      width: mediaSlideItem.fabricObject.getScaledWidth() * fabricObject.scaleX + mediaSlideItem.fabricObject.strokeWidth / fabricObject.scaleX,
      height: mediaSlideItem.fabricObject.getScaledHeight() * fabricObject.scaleY + mediaSlideItem.fabricObject.strokeWidth / fabricObject.scaleY,
      ...mediaSlideItem.mediaSlide.getMediaSlideAbsolutePosition(),
    });
    this.clipPath.setCoords();
  }

  public setTextItemBlockOrigin(): void {
    const item = this.getAnimatingItem();
    if ((item.isText() || item.isTextSlide()) && !item.hasBackground()) {
      this.block.originY = item.verticalAlign;
    }
  }

  public setMediaItemBlockOrigin(): void {
    const item = this.getAnimatingItem();
    if (item.isImageSlide() || item.isVideoSlide()) {
      if (item.mediaSlide.hasHorizontalAlignmentAxis()) {
        this.block.originX = item.mediaSlide.horizontalAlign;
      } else {
        this.block.originY = item.mediaSlide.verticalAlign;
      }
    }
  }

  public isFrameTimeForAnimation(_time: number): boolean {
    return false;
  }

  public setFrame(time: number): void {
    if (!this.isFrameTimeForAnimation(time) && !this.isTransition) {
      return;
    }
    if (!this.initSetFrame) {
      this.initSetFrame = true;
      this.viewComponentOpacitySnapshot = this.getAnimatingItem().fabricObject.opacity;
      switch (this.type) {
        case AnimationType.WIPE:
        case AnimationType.WIPE_DOWN:
        case AnimationType.WIPE_UP:
        case AnimationType.WIPE_LEFT:
        case AnimationType.WIPE_RIGHT:
        case AnimationType.ZOOM:
        case AnimationType.PEEK:
        case AnimationType.PEEK_DOWN:
        case AnimationType.PEEK_UP:
        case AnimationType.PEEK_LEFT:
        case AnimationType.PEEK_RIGHT:
          this.setClippedItem();
          this.initClipPathOnObjectAnimation();
          this.internalOnComplete = (): void => {
            if (this.clippedItem !== this.item.fabricObject) {
              this.item.page.fabricCanvas.remove(this.clippedItem);
              this.getAnimatingItem().fabricObject.set({opacity: this.viewComponentOpacitySnapshot});
            }
          };
          break;

        case AnimationType.BLOCK:
          this.initBlockAnimationItems();
          this.internalOnComplete = (): void => {
            this.item.page.fabricCanvas.remove(this.block);
          };
          break;

        default:
          this.animatingItem = this.getAnimatingItem().fabricObject;
          break;
      }

      this.initAnimation();
    }

    const easeFunction = this.getEaseFunctionForAnimationStyle();
    const vals: Record<string, number> = {};

    Object.keys(this.startingAnimationValues).forEach((key) => {
      vals[key] = easeFunction(
        this.isTransition ? Math.min(time, this.getAnimationTimeForItem()) : this.getAnimationTimeForEaseFunction(time),
        this.startingAnimationValues[key],
        this.endingAnimationValues[key] - this.startingAnimationValues[key],
        this.getAnimationTimeForItem()
      );
    });

    if (this.shouldClipForAnimation(this.type) && (this.item.isImage() || this.item.isImageSlide()) && this.item.isMasked()) {
      this.getAnimatingItem().fabricObject.set('opacity', 0);
    }

    this.animatingItem.set(vals);
    this.onAnimationChange();
    if (!this.isFinalFrameSet && time >= (this.isTransition ? this.getAnimationTimeForItem() : this.getAnimationTimeForFinalFrame())) {
      this.getAnimatingItem().fabricObject.set(this.onCompleteValues);
      if (this.internalOnComplete) {
        this.internalOnComplete();
      }
      this.isFinalFrameSet = true;
    }
  }

  public shouldBlockBeMadeVisible(): boolean {
    return (
      Math.abs(Math.round(this.block.left - this.startingAnimationValues.left)) - Math.abs(Math.round(this.clipPath.left - this.startingAnimationValues.left)) >= 0 &&
      Math.abs(Math.round(this.block.top - this.startingAnimationValues.top)) - Math.abs(Math.round(this.clipPath.top - this.startingAnimationValues.top)) >= 0
    );
  }

  public getEaseFunctionForAnimationStyle = (): ((timeElapsed: number, startValue: number, byValue: number, duration: number) => number) => {
    switch (this.type) {
      case AnimationType.SLIDE:
      case AnimationType.RISE:
      case AnimationType.PAN:
      case AnimationType.PAN_RIGHT:
      case AnimationType.PAN_LEFT:
      case AnimationType.PAN_DOWN:
      case AnimationType.PAN_UP:
      case AnimationType.SHRINK:
      case AnimationType.EXPAND:
      case AnimationType.TUMBLE:
      case AnimationType.ROTATE:
        return util.ease.easeOutCubic;

      case AnimationType.BLOCK:
        return util.ease.easeInOutQuint;

      case AnimationType.FADE:
        return util.ease.easeOutQuad;

      case AnimationType.SCALE:
      case AnimationType.ZOOM:
        return util.ease.easeInOutQuad;

      case AnimationType.POP:
      case AnimationType.JELLO:
        return this.getEaseFunctionForPopAndJello();

      case AnimationType.BOUNCE_IN_DOWN:
      case AnimationType.BOUNCE:
      case AnimationType.ZOOM_IN:
      case AnimationType.ZOOM_OUT:
        return util.ease.easeOutBack;

      case AnimationType.WIPE:
      case AnimationType.WIPE_DOWN:
      case AnimationType.WIPE_UP:
      case AnimationType.WIPE_LEFT:
      case AnimationType.WIPE_RIGHT:
      case AnimationType.PEEK:
      case AnimationType.PEEK_DOWN:
      case AnimationType.PEEK_UP:
      case AnimationType.PEEK_LEFT:
      case AnimationType.PEEK_RIGHT:
        return util.ease.easeOutSine;

      case AnimationType.NONE:
        return util.ease.easeOutCubic;

      default:
        console.error('Unknown animation type: ', this.type);
        return util.ease.easeOutCubic;
    }
  };

  public shouldClipForAnimation(animationType: AnimationType): boolean {
    return (
      animationType === AnimationType.ZOOM ||
      animationType === AnimationType.WIPE ||
      animationType === AnimationType.WIPE_DOWN ||
      animationType === AnimationType.WIPE_UP ||
      animationType === AnimationType.WIPE_LEFT ||
      animationType === AnimationType.WIPE_RIGHT ||
      animationType === AnimationType.PEEK ||
      animationType === AnimationType.PEEK_DOWN ||
      animationType === AnimationType.PEEK_UP ||
      animationType === AnimationType.PEEK_LEFT ||
      animationType === AnimationType.PEEK_RIGHT
    );
  }

  public getAnimationTimeForItem = (): number => {
    const animationTime = getAnimationTime(this.type, this.speed, this.isTransition);
    if (this.item.isSlideshow() && this.item.getSelectedSlide().getSlideDuration() <= animationTime) {
      return this.item.getSelectedSlide().getSlideDuration() - this.item.getSelectedSlide().getSlideDuration() / 2;
    }
    return animationTime;
  };
}

export const isAnimationApplicableOnItem = (item: ItemType, animationType: AnimationType): boolean => {
  // ALL backgrounds are excluded (ImageBackground sets isBackground in its constructor):
  // backgrounds have no animation UI, so an applied animation could never be removed.
  if (item.isTranscript() || item.isBackground || item.isSlideshowSlide()) {
    return false;
  }
  if (isBlockAnimation(animationType)) {
    return item.isText() || item.isSlideshow();
  }
  return true;
};

/**
 * Serialized-object twin of isAnimationApplicableOnItem, for code that only has redux mirror
 * data. The gitype check alone misses backgrounds whose gitype isn't IMAGEBACKGROUND (solid
 * RECTANGLE and VIDEO backgrounds), so it must also honor the isBackground flag.
 */
export const isAnimationApplicableOnItemObject = (item: Pick<BaseItemObject, 'gitype' | 'isBackground'>, animationType: AnimationType): boolean => {
  return !item.isBackground && isAnimationApplicableForGIType(item.gitype, animationType);
};

export const isAnimationApplicableForGIType = (giType: ITEM_TYPE, animationType: AnimationType): boolean => {
  if ([ITEM_TYPE.TRANSCRIPT, ITEM_TYPE.IMAGEBACKGROUND, ITEM_TYPE.TEXTSLIDE, ITEM_TYPE.IMAGESLIDE, ITEM_TYPE.VIDEOSLIDE].includes(giType)) {
    return false;
  }
  if (isBlockAnimation(animationType)) {
    return [ITEM_TYPE.TEXT, ITEM_TYPE.SLIDESHOW].includes(giType);
  }
  return true;
};

export const isBlockAnimation = (animationType: AnimationType): boolean => {
  return animationType === AnimationType.BLOCK;
};

export const getOutroForIntroAnimation = (animationType: AnimationType): AnimationType => {
  switch (animationType) {
    case AnimationType.WIPE_DOWN:
      return AnimationType.WIPE_UP;

    case AnimationType.WIPE_UP:
      return AnimationType.WIPE_DOWN;

    case AnimationType.WIPE_LEFT:
      return AnimationType.WIPE_RIGHT;

    case AnimationType.WIPE_RIGHT:
      return AnimationType.WIPE_LEFT;

    case AnimationType.PEEK_DOWN:
      return AnimationType.PEEK_UP;

    case AnimationType.PEEK_UP:
      return AnimationType.PEEK_DOWN;

    case AnimationType.PEEK_LEFT:
      return AnimationType.PEEK_RIGHT;

    case AnimationType.PEEK_RIGHT:
      return AnimationType.PEEK_LEFT;

    case AnimationType.SHRINK:
      return AnimationType.EXPAND;

    case AnimationType.PAN_RIGHT:
      return AnimationType.PAN_LEFT;

    case AnimationType.PAN_LEFT:
      return AnimationType.PAN_RIGHT;

    case AnimationType.PAN_DOWN:
      return AnimationType.PAN_UP;

    case AnimationType.PAN_UP:
      return AnimationType.PAN_DOWN;

    default:
      return animationType;
  }
};

export const isWipeOrBlockAnimation = (animationType: AnimationType): boolean => {
  return (
    animationType === AnimationType.WIPE_DOWN ||
    animationType === AnimationType.WIPE_UP ||
    animationType === AnimationType.WIPE_LEFT ||
    animationType === AnimationType.WIPE_RIGHT ||
    animationType === AnimationType.BLOCK ||
    animationType === AnimationType.WIPE
  );
};
